import React, { createContext, useContext, useEffect, useMemo } from 'react';
import {
  TenantFeatureKey,
  TenantMenuItemKey,
  TenantPageEntryPointKey,
  TenantRouteKey,
  TenantTabKey,
} from './features';
import { TenantConfig } from './types';

interface TenantContextValue {
  tenant: TenantConfig;
  tenantKey: string;
  branding: TenantConfig['branding'];
  features: TenantConfig['features'];
  routeConfig: TenantConfig['visibility']['routes'];
  isFeatureEnabled: (feature: TenantFeatureKey) => boolean;
  isRouteVisible: (route: TenantRouteKey) => boolean;
  isMenuItemVisible: (menuItem: TenantMenuItemKey) => boolean;
  isTabVisible: (tab: TenantTabKey) => boolean;
  isPageEntryPointVisible: (entryPoint: TenantPageEntryPointKey) => boolean;
}

const TenantContext = createContext<TenantContextValue | undefined>(undefined);

const ensureFaviconLink = () => {
  let favicon = document.querySelector(
    "link[rel='icon']"
  ) as HTMLLinkElement | null;

  if (!favicon) {
    favicon = document.createElement('link');
    favicon.rel = 'icon';
    document.head.appendChild(favicon);
  }

  return favicon;
};

export const TenantProvider: React.FC<{
  config: TenantConfig;
  children: React.ReactNode;
}> = ({ config, children }) => {
  useEffect(() => {
    document.title = config.branding.appName;
    document.documentElement.setAttribute('data-tenant', config.key);

    const favicon = ensureFaviconLink();
    favicon.href = config.branding.faviconUrl;

    const themeColor = document.querySelector(
      "meta[name='theme-color']"
    ) as HTMLMetaElement | null;

    if (themeColor) {
      themeColor.content = config.branding.colors.primary;
    }
  }, [config]);

  const value = useMemo<TenantContextValue>(() => {
    const { visibility } = config;

    return {
      tenant: config,
      tenantKey: config.key,
      branding: config.branding,
      features: config.features,
      routeConfig: visibility.routes,
      isFeatureEnabled: (feature) => config.features[feature] !== false,
      isRouteVisible: (route) => visibility.routes[route] !== false,
      isMenuItemVisible: (menuItem) => visibility.menuItems[menuItem] !== false,
      isTabVisible: (tab) => visibility.tabs[tab] !== false,
      isPageEntryPointVisible: (entryPoint) =>
        visibility.pageEntryPoints[entryPoint] !== false,
    };
  }, [config]);

  return (
    <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
  );
};

export const useTenant = () => {
  const context = useContext(TenantContext);

  if (!context) {
    throw new Error('useTenant must be used within TenantProvider');
  }

  return context;
};
